import { Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';

import LoanApi from '@portal/repositories/loan';

import SettingsApi from '~/repositories/settings';

import {
  SET_LOAN_SETTINGS,
  SET_REMOTE_CONFIG,
  SET_SETTINGS,
  SET_SETTINGS_ERROR,
  SET_SETTINGS_SUCCESS,
  SET_SETTINGS_KEYS,
} from './actionTypes';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type SetSettingsErrorAction = {
  payload: Error | null;
  type: string;
};

export type SetSettingsSuccessAction = {
  payload: string | null;
  type: string;
};

export const setSettingsError =
  (payload: SetSettingsErrorAction['payload']) =>
  (
    dispatch: Dispatch<
      SetSettingsSuccessAction | SetSettingsErrorAction | SetSettingsErrorAction
    >
  ) => {
    dispatch({
      type: SET_SETTINGS_ERROR,
      payload,
    });
    dispatch({
      type: SET_SETTINGS_SUCCESS,
      payload: null,
    });
  };

export type SetRemoteConfigAction = {
  payload: models.RemoteConfig;
  type: string;
};

export const getRemoteConfig =
  (): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    SetRemoteConfigAction | SetSettingsErrorAction | LoadingAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await SettingsApi.getRemoteConfig();

      dispatch({
        type: SET_REMOTE_CONFIG,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSettingsError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type SetSettingsAction = {
  payload: models.Settings;
  type: string;
};

export const getSettings =
  (): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    SetSettingsAction | SetSettingsErrorAction | LoadingAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await SettingsApi.get();

      dispatch({
        type: SET_SETTINGS,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSettingsError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type SetLoanSettingsAction = {
  payload: models.LoanSettings;
  type: string;
};

export const getLoanSettings =
  (): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    SetLoanSettingsAction | SetSettingsErrorAction | LoadingAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await LoanApi.getLoanSettings();

      dispatch({
        type: SET_LOAN_SETTINGS,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSettingsError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type SetSettingsKeysAction = {
  payload: models.SettingsKeys;
  type: string;
};

export const getSettingsKeys =
  (
    key: string[]
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    SetSettingsKeysAction | SetSettingsErrorAction | LoadingAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await SettingsApi.setKeys(key);

      dispatch({
        type: SET_SETTINGS_KEYS,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch(setSettingsError(err));
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
